import React from 'react';
import EduTip from './EduTip.jsx';
import Select from './Select.jsx';
import { FVF_DRIVERS } from '../data.js';
import { formatPlainNumber } from '../formatting.js';

function Row({ label, term, hint, children }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-[10px] items-center py-1 border-b border-dashed border-[rgba(148,163,184,0.12)] last:border-b-0">
      <div className="flex items-center gap-[6px] text-[#cbd5e1] text-[12px]">
        <span>{label}</span>
        {term && <EduTip term={term} />}
      </div>
      <div className="text-[12px]">{children}</div>
      {hint && <div className="col-start-2 text-[#64748b] text-[10px] italic">{hint}</div>}
    </div>
  );
}

export default function DriverPanel({ scenario, setScenario }) {
  const set = (patch) => setScenario(s => ({ ...s, ...patch }));
  const method = scenario.fvfMethod ?? 'triangulated';

  const title = 'm-0 text-[13px] text-[#f1f5f9] font-bold tracking-[0.02em] flex items-center gap-2';
  const titleGap = 'mt-3 pt-3 border-t border-dashed border-[#334155]';
  const sub = 'mt-2 mb-0 text-[11px] text-[#94a3b8] font-semibold tracking-[0.06em] uppercase flex items-center gap-[6px]';
  const pillBase = 'inline-flex items-center gap-[6px] px-[10px] py-[6px] rounded-[18px] border text-[11px] font-semibold cursor-pointer transition-all duration-150 capitalize';

  return (
    <div className="flex flex-col gap-[10px]">
      <h4 className={title}>Universal Model Driver Methodology</h4>

      <div className="flex gap-2 flex-wrap mb-2">
        {FVF_DRIVERS.methods.map(m => {
          const active = method === m;
          const state = active
            ? 'bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] border-transparent text-[#f8fafc]'
            : 'border-[#334155] bg-[rgba(15,23,42,0.4)] text-[#cbd5e1] hover:border-accent-blue hover:text-[#93c5fd]';
          return (
            <button
              key={m}
              type="button"
              className={`${pillBase} ${state}`}
              onClick={() => set({ fvfMethod: m })}
            >
              <span>{m}</span>
              <EduTip term={m === 'top-down' ? 'Top-down' : m === 'bottom-up' ? 'Bottom-up' : 'Triangulated'} />
            </button>
          );
        })}
      </div>

      {(method === 'top-down' || method === 'triangulated') && (
        <>
          <h5 className={sub}>Top-down inputs <EduTip term="Top-down" /></h5>
          <Row label="Market share / reach (Y1)" hint={`Addressable unit pool: ${formatPlainNumber(FVF_DRIVERS.topDown.tamTenants, { decimals: 0, scale: false })} qualified units`}>
            <Select
              compact
              value={scenario.fvfTopDown_marketShare ?? FVF_DRIVERS.topDown.marketSharePctY1}
              onChange={(e) => set({ fvfTopDown_marketShare: parseFloat(e.target.value) })}
            >
              {FVF_DRIVERS.topDown.marketShare_options.map(v => (
                <option key={v} value={v}>{`${(v * 100).toFixed(3)}% (${Math.round(FVF_DRIVERS.topDown.tamTenants * v)} units)`}</option>
              ))}
            </Select>
          </Row>
          <Row label="Avg recurring revenue / unit" term="ARPU">
            <Select
              compact
              value={scenario.fvfTopDown_arpu ?? FVF_DRIVERS.topDown.arpuPerMonth}
              onChange={(e) => set({ fvfTopDown_arpu: parseFloat(e.target.value) })}
            >
              {FVF_DRIVERS.topDown.arpuPerMonth_options.map(v => <option key={v} value={v}>{`${v}/mo`}</option>)}
            </Select>
          </Row>
        </>
      )}

      {(method === 'bottom-up' || method === 'triangulated') && (
        <>
          <h5 className={sub}>Bottom-up inputs <EduTip term="Bottom-up" /></h5>
          <Row label="Initial active units">
            <Select
              compact
              value={scenario.fvfBottomUp_initialTenants ?? FVF_DRIVERS.bottomUp.initialTenants}
              onChange={(e) => set({ fvfBottomUp_initialTenants: parseInt(e.target.value, 10) })}
            >
              {FVF_DRIVERS.bottomUp.initialTenants_options.map(v => <option key={v} value={v}>{v}</option>)}
            </Select>
          </Row>
          <Row label="Net new units / month">
            <Select
              compact
              value={scenario.fvfBottomUp_newPerMonth ?? FVF_DRIVERS.bottomUp.monthlyNewTenants}
              onChange={(e) => set({ fvfBottomUp_newPerMonth: parseInt(e.target.value, 10) })}
            >
              {FVF_DRIVERS.bottomUp.monthlyNewTenants_options.map(v => <option key={v} value={v}>{v}</option>)}
            </Select>
          </Row>
          <Row label="Monthly churn" term="Churn">
            <Select
              compact
              value={scenario.fvfBottomUp_churn ?? FVF_DRIVERS.bottomUp.churnRateMonthly}
              onChange={(e) => set({ fvfBottomUp_churn: parseFloat(e.target.value) })}
            >
              {FVF_DRIVERS.bottomUp.churnRate_options.map(v => <option key={v} value={v}>{`${(v * 100).toFixed(0)}%`}</option>)}
            </Select>
          </Row>
          <Row label="Avg recurring revenue / unit" term="ARPU">
            <Select
              compact
              value={scenario.fvfBottomUp_arpu ?? FVF_DRIVERS.bottomUp.arpuPerMonth}
              onChange={(e) => set({ fvfBottomUp_arpu: parseFloat(e.target.value) })}
            >
              {FVF_DRIVERS.bottomUp.arpuPerMonth_options.map(v => <option key={v} value={v}>{`${v}/mo`}</option>)}
            </Select>
          </Row>
        </>
      )}

      {method === 'triangulated' && (
        <p className="m-0 text-[#94a3b8] text-[11px] leading-[1.5]">
          Blend: <strong className="text-text">{(FVF_DRIVERS.triangulated.weights.bottomUp * 100).toFixed(0)}% bottom-up</strong>
          {' + '}<strong className="text-text">{(FVF_DRIVERS.triangulated.weights.topDown * 100).toFixed(0)}% top-down</strong>
          {' + '}<strong className="text-text">{(FVF_DRIVERS.triangulated.weights.scenarioMRR * 100).toFixed(0)}% target recurring revenue</strong>.
          Anchored in operating reality, stretched by strategic ambition.
        </p>
      )}
    </div>
  );
}
